import { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api-config';
import { CB_GCP_API_KEY, TENANT } from '../../config/app-config';
import { IApiHooksWithTrigger } from '../../interfaces/app-interfaces';
import { IGCPChatApiResponse } from '../../interfaces/gcp-api-response';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { isWithinBusinessHours } from '../../utils/gcp-utils';

const useGCPChat = (locale: string): IApiHooksWithTrigger<IGCPChatApiResponse> => {
  const { logCustomAttributesNR } = useNRLogging();

  const feature = 'ContactUsPage';
  const REQUEST_URL = `${API_ENDPOINTS.GCP}/v3/channels/chat?tenant=${TENANT}&locale=${locale}&feature=${feature}`;
  const [data, setData] = useState<IGCPChatApiResponse | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [toggle, setToggle] = useState(false);
  const trigger = useCallback(() => {
    setToggle(true);
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      if (toggle) {
        setLoading(true);
        try {
          logCustomAttributesNR('ca-chatbot-misc-api-request', {
            requestUrl: REQUEST_URL,
            feature,
            tenant: TENANT,
            locale,
            logLevel: 'info',
            message: 'Fetching GCP Chat API data',
            apiType: 'getGCPData',
          });
          const response = await fetch(REQUEST_URL, {
            headers: {
              'x-api-key': CB_GCP_API_KEY,
            },
          });

          if (!response.ok) {
            // If response is not ok (status not in the range 200-299),
            // throw an error to skip the next then block
            const error = new Error(`API responded with status: ${response.status}`);
            error.name = 'ApiError';
            throw error;
          }

          const responseData = (await response.json()) as IGCPChatApiResponse;

          console.log('####', responseData);

          let updatedResponseData;
          if (responseData) {
            updatedResponseData = { ...responseData };
            if (!responseData.status.isChatEnabled && responseData.schedule.IsActive === false) {
              updatedResponseData.status.isEmodeEnabled = isWithinBusinessHours(responseData);
            } else {
              updatedResponseData.status.isEmodeEnabled = false;
            }
            setData(updatedResponseData);
          } else setData(responseData);
          logCustomAttributesNR('ca-chatbot-misc-api-response', {
            logLevel: 'info',
            message: 'Fetching GCP Chat API data success',
            apiType: 'getGCPData',
            chatStatus: responseData?.status.isChatEnabled,
          });
        } catch (error) {
          console.error(error);
          setError(new Error());
          logCustomAttributesNR('ca-chatbot-misc-api-error', {
            errorName: error?.name,
            errorStack: error?.stack,
            message: error?.message,
            logLevel: 'error',
            apiType: 'getGCPData',
          });
          setData(undefined);
        } finally {
          setLoading(false);
          setToggle(false);
        }
      }
    };
    fetchData();
  }, [locale, toggle]);
  return { data, loading, error, trigger };
};
export { useGCPChat };
